import { BodyText } from "@/components/ui";
import { Badge } from "@/components/ui/Badge";
import { Checkbox } from "@/components/ui/Checkbox";
import { Label } from "@/components/ui/Label";
import { cn } from "@/lib/utils";
import { Ban, TriangleAlert } from "lucide-react";
import type { RenewablePrescriptionsForDrug } from "../network/useRenewablePrescriptions";

export const DrugGroup = ({
  drugGroup,
  selectedMedReqIds,
  selectedDrugCodes,
  addSelectedMedReqId,
  removeSelectedMedReqId,
  addSelectedDrugCode,
  removeSelectedDrugCode,
}: {
  drugGroup: RenewablePrescriptionsForDrug;
  selectedMedReqIds: string[];
  selectedDrugCodes: string[];
  addSelectedMedReqId: (id: string) => void;
  removeSelectedMedReqId: (id: string) => void;
  addSelectedDrugCode: (code: string) => void;
  removeSelectedDrugCode: (code: string) => void;
}) => {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row gap-2 items-center">
        <BodyText
          size="sm"
          bold
          textColor={drugGroup.isObsolete ? "tertiary" : "primary"}
          className="flex-1"
        >
          {drugGroup.drugName}
        </BodyText>
        {drugGroup.isObsolete && (
          <Badge variant="light">
            <Ban className="h-2 w-2 stroke-tertiary" />
            <BodyText textColor="tertiary" size="sm">
              Unavailable
            </BodyText>
          </Badge>
        )}
      </div>
      <div className="flex flex-col gap-1">
        {drugGroup.renewablePrescriptions.map(
          ({ medicationRequest, isDuplicate }) => (
            <div
              className="flex flex-row gap-1 items-center"
              key={`${drugGroup.drugIdentifier}-${medicationRequest.id}`}
            >
              <Checkbox
                id={medicationRequest.id as string}
                data-testid={`renewal-checkbox-${medicationRequest.id}`}
                disabled={drugGroup.isObsolete}
                checked={selectedMedReqIds.includes(
                  medicationRequest.id as string,
                )}
                onCheckedChange={(checked) => {
                  if (checked) {
                    addSelectedMedReqId(medicationRequest.id as string);
                  } else {
                    removeSelectedMedReqId(medicationRequest.id as string);
                  }
                }}
              />
              <Label
                htmlFor={medicationRequest.id as string}
                className={cn(
                  "font-normal",
                  drugGroup.isObsolete ? "text-tertiary" : "text-primary",
                )}
              >
                {`${medicationRequest.dispenseQuantity} Qty, ${medicationRequest.daysSupply} DS, ${medicationRequest.refills} RF, ${medicationRequest.sig}`}
              </Label>
              {isDuplicate && (
                <Badge variant="warning">
                  <TriangleAlert className="h-2 w-2 stroke-warning" />
                  <BodyText textColor="warning" size="sm">
                    Duplicate
                  </BodyText>
                </Badge>
              )}
            </div>
          ),
        )}
        <div className="flex flex-row gap-1 items-center">
          <Checkbox
            id={drugGroup.drugIdentifier}
            data-testid={`renewal-checkbox-${drugGroup.drugIdentifier}`}
            disabled={drugGroup.isObsolete}
            checked={selectedDrugCodes.includes(drugGroup.drugIdentifier)}
            onCheckedChange={(checked) => {
              if (checked) {
                addSelectedDrugCode(drugGroup.drugIdentifier);
              } else {
                removeSelectedDrugCode(drugGroup.drugIdentifier);
              }
            }}
          />
          <Label
            htmlFor={drugGroup.drugIdentifier}
            className={cn(
              "font-normal",
              drugGroup.isObsolete ? "text-tertiary" : "text-link",
            )}
          >
            Add as blank prescription
          </Label>
        </div>
      </div>
    </div>
  );
};
